// ShiftTenantModal.jsx
import React, { useState, useEffect } from 'react';
import { searchTenantsForPG, getVacantBeds, postShiftTenant } from '../apis/services/pgApi';

// Helper function to get today's date in IST
const getTodayIST = () => {
  const now = new Date();
  const ist = new Date(
    now.toLocaleString("en-US", {
      timeZone: "Asia/Kolkata",
    })
  );
  const yyyy = ist.getFullYear();
  const mm = String(ist.getMonth() + 1).padStart(2, "0");
  const dd = String(ist.getDate()).padStart(2, "0");
  return `${yyyy}-${mm}-${dd}`;
};

const ShiftTenantModal = ({ isOpen, onClose, selectedPG, onSuccess }) => {
  // Form states
  const [tenantId, setTenantId] = useState("");
  const [tenantList, setTenantList] = useState([]);
  const [loadingTenants, setLoadingTenants] = useState(false);

  // Room/Bed states
  const [roomType, setRoomType] = useState("");
  const [newRoomId, setNewRoomId] = useState("");
  const [newBedId, setNewBedId] = useState("");
  const [vacantBeds, setVacantBeds] = useState([]);
  const [loadingBeds, setLoadingBeds] = useState(false);

  // Other form fields
  const [newRoomRent, setNewRoomRent] = useState("");
  const [effectiveDate, setEffectiveDate] = useState(getTodayIST());
  const [shiftReason, setShiftReason] = useState("");
  const [remarks, setRemarks] = useState("");
  const [shiftCharge, setShiftCharge] = useState("");
  const [oldRoomMeterReading, setOldRoomMeterReading] = useState("");
  const [newRoomOpeningReading, setNewRoomOpeningReading] = useState("");

  // UI states
  const [error, setError] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const roomTypeOptions = [
    { value: "Single", label: "Single" },
    { value: "Double", label: "Double" },
    { value: "Triple", label: "Triple" },
    { value: "Quad", label: "Quad" },
    { value: "Dorm", label: "Dorm" }
  ];

  // Fetch tenants when modal opens
  useEffect(() => {
    if (isOpen && selectedPG) {
      fetchTenants();
    }
  }, [isOpen, selectedPG]);

  // Fetch vacant beds when room type changes
  useEffect(() => {
    if (roomType && selectedPG?.PGID) {
      fetchVacantBeds();
    } else {
      setVacantBeds([]);
      setNewBedId("");
    }
  }, [roomType, selectedPG]);

  const fetchTenants = async () => {
    setLoadingTenants(true);
    setError("");
    try {
      const response = await searchTenantsForPG(selectedPG?.PGID);
      console.log("Tenants response:", response);

      if (response && response.data && response.data.length > 0) {
        setTenantList(response.data);
      } else {
        setTenantList([]);
        setError("No active tenants found for this PG");
      }
    } catch (err) {
      console.error("Error fetching tenants:", err);
      setError("Failed to load tenants. Please try again.");
      setTenantList([]);
    } finally {
      setLoadingTenants(false);
    }
  };

  const fetchVacantBeds = async () => {
    setLoadingBeds(true);
    setError("");
    try {
      const response = await getVacantBeds(selectedPG?.PGID, roomType);
      console.log("Vacant beds response:", response);
      
      if (response && response.length > 0) {
        setVacantBeds(response);
      } else {
        setVacantBeds([]);
        setError("No vacant beds available for this room type");
      }
    } catch (err) {
      console.error("Error fetching vacant beds:", err);
      setError("Failed to load vacant beds");
      setVacantBeds([]);
    } finally {
      setLoadingBeds(false);
    }
  };

  if (!isOpen) return null;

  const resetForm = () => {
    setTenantId("");
    setRoomType("");
    setNewRoomId("");
    setNewBedId("");
    setNewRoomRent("");
    setEffectiveDate(getTodayIST());
    setShiftReason("");
    setRemarks("");
    setShiftCharge("");
    setOldRoomMeterReading("");
    setNewRoomOpeningReading("");
    setError("");
  };

  const handleClose = () => {
    resetForm();
    onClose();
  };

  const handleSubmit = async () => {
    // Validations
    if (!tenantId) {
      setError("Please select a tenant");
      return;
    }
    if (!roomType) {
      setError("Please select a room type");
      return;
    }
    if (!newBedId) {
      setError("Please select a bed");
      return;
    }
    if (!newRoomRent || parseFloat(newRoomRent) <= 0) {
      setError("Please enter a valid room rent amount");
      return;
    }
    if (!effectiveDate) {
      setError("Please select an effective date");
      return;
    }

    // Get selected tenant details
    const selectedTenant = tenantList.find(t => String(t.tenantId) === String(tenantId));
    if (!selectedTenant) {
      setError("Selected tenant not found");
      return;
    }

    // Get selected bed details
    const selectedBed = vacantBeds.find(b => String(b.BedID) === String(newBedId));
    if (!selectedBed) {
      setError("Selected bed not found");
      return;
    }

    // Get user ID from localStorage
    const userId = parseInt(localStorage.getItem('userId')) || 1;

    // Prepare payload
    const payload = {
      AdmissionID: selectedTenant.admissionId,
      NewRoomID: selectedBed.RoomID || parseInt(newRoomId), // Use RoomID from bed or manual entry
      NewBedID: parseInt(newBedId),
      NewRoomrent: parseFloat(newRoomRent),
      EffectiveDate: effectiveDate,
      ShiftReason: shiftReason.trim() || null,
      Remarks: remarks.trim() || null,
      ShiftCharge: shiftCharge ? parseFloat(shiftCharge) : 0,
      CreatedBy: userId,
      OldRoomMeterReading: oldRoomMeterReading ? parseFloat(oldRoomMeterReading) : null,
      NewRoomOpeningReading: newRoomOpeningReading ? parseFloat(newRoomOpeningReading) : null
    };

    console.log("Shift tenant payload:", payload);

    setError("");
    setSubmitting(true);

    try {
      // Call the API to shift tenant
      const response = await postShiftTenant(payload);
      console.log("Shift tenant response:", response);

      if (response.success !== false && response.Status !== 0) {
        alert(`Tenant shifted successfully! Shift ID: ${response.data.ShiftID || 'N/A'}`);
        
        // Call onSuccess callback if provided
        if (onSuccess) {
          await onSuccess(response);
        }

        resetForm();
        onClose();
      } else {
        setError(response.Message || "Failed to shift tenant");
      }
    } catch (err) {
      console.error("Error shifting tenant:", err);
      const errorMessage = err.response?.data?.message || err.message || "Failed to shift tenant. Please try again.";
      setError(errorMessage);
    } finally {
      setSubmitting(false);
    }
  };

  // Get tenant display name
  const getTenantDisplayName = (tenant) => {
    if (!tenant) return "";
    return `${tenant.tenantName || 'Unknown'} · Room ${tenant.roomNumber || '—'} · Bed ${tenant.bedNumber || '—'}`;
  };

  // Get selected bed display
  const selectedBed = vacantBeds.find(b => String(b.BedID) === String(newBedId));
  const selectedBedLabel = selectedBed
    ? `${selectedBed.VACANT} — ${selectedBed.RoomType}`
    : newBedId
      ? `Bed ID ${newBedId}`
      : "Not selected";

  return (
    <div className="rv-overlay" onClick={handleClose}>
      <div className="rv-modal" onClick={(e) => e.stopPropagation()}>
        {/* Header */}
        <div className="rv-head">
          <div>
            <h2 className="rv-title">Switch Room</h2>
            <p className="rv-sub">{selectedPG?.PGName ? `For ${selectedPG.PGName}` : "Shift tenant to a new room"}</p>
          </div>
          <button className="rv-close" onClick={handleClose}>
            <svg width="15" height="15" viewBox="0 0 18 18" fill="none">
              <path d="M1 1L17 17M17 1L1 17" stroke="#374151" strokeWidth="2" strokeLinecap="round" />
            </svg>
          </button>
        </div>

        <div className="rv-body">
          {/* Tenant Selection */}
          <div className="rv-owner-field">
            <label className="rv-owner-label">Select Tenant</label>
            <select
              className="rv-owner-select"
              value={tenantId}
              onChange={(e) => setTenantId(e.target.value)}
              disabled={loadingTenants}
            >
              <option value="">
                {loadingTenants ? "Loading tenants..." : "Choose tenant…"}
              </option>
              {tenantList.map((tenant) => (
                <option key={tenant.tenantId} value={tenant.tenantId}>
                  {getTenantDisplayName(tenant)}
                </option>
              ))}
            </select>
            {tenantList.length === 0 && !loadingTenants && (
              <p style={{ color: '#6B7280', fontSize: '12px', marginTop: '4px' }}>
                No active tenants found. Please add tenants first.
              </p>
            )}
          </div>

          {/* Room Type and Bed Selection */}
          <div className="rv-owner-grid" style={{ marginTop: '14px' }}>
            <div className="rv-owner-field">
              <label className="rv-owner-label">New Room Type</label>
              <select
                className="rv-owner-select"
                value={roomType}
                onChange={(e) => setRoomType(e.target.value)}
              >
                <option value="">Select room type</option>
                {roomTypeOptions.map(opt => (
                  <option key={opt.value} value={opt.value}>{opt.label}</option>
                ))}
              </select>
            </div>

            <div className="rv-owner-field">
              <label className="rv-owner-label">Select New Bed</label>
              <select
                className="rv-owner-select"
                value={newBedId}
                onChange={(e) => setNewBedId(e.target.value)}
                disabled={loadingBeds || vacantBeds.length === 0 || !roomType}
              >
                <option value="">
                  {loadingBeds ? "Loading..." : 
                   !roomType ? "Select room type first" :
                   vacantBeds.length === 0 ? "No beds available" : 
                   "Select bed"}
                </option>
                {vacantBeds.map(bed => (
                  <option key={bed.BedID} value={bed.BedID}>
                    {bed.VACANT} - {bed.RoomType}
                  </option>
                ))}
              </select>
              {selectedBed && (
                <span style={{ fontSize: '12px', color: '#6B7280', marginTop: '4px', display: 'block' }}>
                  Selected: {selectedBedLabel}
                </span>
              )}
            </div>
          </div>

          {/* New Room Rent and Effective Date */}
          <div className="rv-owner-grid" style={{ marginTop: '14px' }}>
            <div className="rv-owner-field">
              <label className="rv-owner-label">New Room Rent (₹)</label>
              <input
                type="number"
                min="0"
                step="0.01"
                className="rv-owner-input"
                placeholder="e.g. 8000"
                value={newRoomRent}
                onChange={(e) => setNewRoomRent(e.target.value)}
              />
            </div>
            <div className="rv-owner-field">
              <label className="rv-owner-label">Effective Date</label>
              <input
                type="date"
                className="rv-owner-input"
                value={effectiveDate}
                onChange={(e) => setEffectiveDate(e.target.value)}
                min={getTodayIST()}
              />
            </div>
          </div>

          {/* Shift Reason and Remarks */}
          <div className="rv-owner-field" style={{ marginTop: '14px' }}>
            <label className="rv-owner-label">Shift Reason</label>
            <input
              type="text"
              className="rv-owner-input"
              placeholder="e.g. Tenant requested room change"
              value={shiftReason}
              onChange={(e) => setShiftReason(e.target.value)}
            />
          </div>

          <div className="rv-owner-field" style={{ marginTop: '14px' }}>
            <label className="rv-owner-label">Remarks</label>
            <textarea
              className="rv-owner-input"
              rows={2}
              style={{ resize: 'vertical', fontFamily: 'inherit' }}
              placeholder="Additional remarks (optional)"
              value={remarks}
              onChange={(e) => setRemarks(e.target.value)}
            />
          </div>

          {/* Shift Charge and Meter Readings */}
          <div className="rv-owner-grid" style={{ marginTop: '14px' }}>
            <div className="rv-owner-field">
              <label className="rv-owner-label">Shift Charge (₹)</label>
              <input
                type="number"
                min="0"
                step="0.01"
                className="rv-owner-input"
                placeholder="e.g. 500"
                value={shiftCharge}
                onChange={(e) => setShiftCharge(e.target.value)}
              />
            </div>
          </div>

          <div className="rv-owner-grid" style={{ marginTop: '14px' }}>
            <div className="rv-owner-field">
              <label className="rv-owner-label">Old Room Meter Reading</label>
              <input
                type="number"
                min="0"
                step="0.01"
                className="rv-owner-input"
                placeholder="e.g. 1250.50"
                value={oldRoomMeterReading}
                onChange={(e) => setOldRoomMeterReading(e.target.value)}
              />
            </div>
            <div className="rv-owner-field">
              <label className="rv-owner-label">New Room Opening Reading</label>
              <input
                type="number"
                min="0"
                step="0.01"
                className="rv-owner-input"
                placeholder="e.g. 0.00"
                value={newRoomOpeningReading}
                onChange={(e) => setNewRoomOpeningReading(e.target.value)}
              />
            </div>
          </div>

          {/* Error Message */}
          {error && (
            <p className="rv-error" style={{ color: '#DC2626', fontSize: '12px', marginTop: '12px' }}>
              {error}
            </p>
          )}
        </div>

        {/* Footer */}
        <div className="rv-footer">
          <button className="rv-btn rv-btn-outline" onClick={handleClose} disabled={submitting}>
            Cancel
          </button>
          <button
            className="rv-btn rv-btn-verify"
            disabled={submitting || loadingTenants || loadingBeds}
            onClick={handleSubmit}
          >
            {submitting ? "Shifting..." : "Switch Room"}
          </button>
        </div>
      </div>
    </div>
  );
};

export default ShiftTenantModal;